import { useCallback, useState } from 'react';
import { useLexChatStore } from '../../matters/ai/lexChatStore';
import { taskApi } from '../../tasks/api/taskApi';

interface UseNoteContextActionsParams {
  selectedText: string;
  noteTitle: string;
  noteId: string;
  matterId?: string;
  clientId?: string;
  closeContextMenu: () => void;
}

interface TaskFeedback {
  type: 'success' | 'error';
  message: string;
}

// Sub-action prompts for Ask Lex
const ASK_LEX_PROMPTS = {
  analyze: 'Provide a detailed legal analysis of this text.',
  counterArguments: 'Identify potential counter-arguments and weaknesses.',
  identifyRisks: 'Identify legal risks, liabilities, and exposure in this text.',
  plainEnglish: 'Rewrite this in plain English suitable for a non-lawyer client.',
} as const;

// Sub-action targets for AI Rewrite
const REWRITE_TARGETS = {
  formalize: 'formal court filing language',
  simplify: 'simplified client letter',
  summarize: 'concise summary',
  expand: 'expanded with additional detail and supporting reasoning',
} as const;

export type AskLexAction = keyof typeof ASK_LEX_PROMPTS;
export type RewriteAction = keyof typeof REWRITE_TARGETS;
export type PrivilegeLevel = 'public' | 'attorney-work-product' | 'attorney-client-privilege';

/**
 * Opens the Lex sidebar and dispatches a message via custom event.
 * UniversalLexAssistant listens for `lex:sendMessage` and runs it
 * through its native handleSendMessage flow (Socket.IO, universalLexClient, status bubbles).
 */
const dispatchToLex = (message: string) => {
  useLexChatStore.getState().setLexOpen(true);
  requestAnimationFrame(() => {
    window.dispatchEvent(new CustomEvent('lex:sendMessage', { detail: { message } }));
  });
};

export function useNoteContextActions({
  selectedText,
  noteTitle,
  noteId,
  matterId,
  clientId,
  closeContextMenu,
}: UseNoteContextActionsParams) {
  const [taskFeedback, setTaskFeedback] = useState<TaskFeedback | null>(null);

  // ── Action Handlers ──────────────────────────────────────────────

  const handleAskLex = useCallback(
    (action: AskLexAction) => {
      closeContextMenu();
      const prompt = ASK_LEX_PROMPTS[action];
      const message = `[Document Analysis Request] Analyze the following excerpt from my legal notes titled "${noteTitle}":\n\n"${selectedText}"\n\n${prompt}`;
      dispatchToLex(message);
    },
    [selectedText, noteTitle, closeContextMenu],
  );

  const handleRewrite = useCallback(
    (action: RewriteAction) => {
      closeContextMenu();
      const target = REWRITE_TARGETS[action];
      const message = `[Document Analysis Request] Rewrite the following text from my legal notes.\n\nTarget: ${target}\n\nOriginal text:\n"${selectedText}"`;
      dispatchToLex(message);
    },
    [selectedText, closeContextMenu],
  );

  const handleCitationLookup = useCallback(() => {
    closeContextMenu();
    const message = `[Legal Research Request] Find legal citations, case law, and current validity status for:\n\n"${selectedText}"`;
    dispatchToLex(message);
  }, [selectedText, closeContextMenu]);

  const handleCreateTask = useCallback(async () => {
    closeContextMenu();
    try {
      await taskApi.create({
        title: selectedText.slice(0, 100) + (selectedText.length > 100 ? '...' : ''),
        description: `From note "${noteTitle}":\n\n${selectedText}`,
        matter_id: matterId,
        client_id: clientId,
        priority: 'medium',
        source: 'manual',
      });
      setTaskFeedback({ type: 'success', message: 'Task created' });
    } catch {
      setTaskFeedback({ type: 'error', message: 'Failed to create task' });
    }
    setTimeout(() => setTaskFeedback(null), 3000);
  }, [selectedText, noteTitle, matterId, clientId, closeContextMenu]);

  const handleAddToTimeline = useCallback(() => {
    closeContextMenu();
    const message = `[Document Analysis Request] Extract the date and event description from this text and format as a structured timeline entry:\n\n"${selectedText}"`;
    dispatchToLex(message);
  }, [selectedText, closeContextMenu]);

  return {
    handleAskLex,
    handleRewrite,
    handleCitationLookup,
    handleCreateTask,
    handleAddToTimeline,
    taskFeedback,
  };
}
